import { DESKTOP_GAME_SOURCE_CONTRACT } from "./sourceContract";

export const DESKTOP_ARTBOARD = DESKTOP_GAME_SOURCE_CONTRACT.artboard;

export interface DesktopFrameSize {
  width: number;
  height: number;
}

export interface DesktopArtboardLayout {
  scale: number;
  offsetX: number;
  offsetY: number;
  renderedWidth: number;
  renderedHeight: number;
}

const finitePositive = (value: number): number => (
  Number.isFinite(value) ? Math.max(0, value) : 0
);
const rounded = (value: number): number => Number(value.toFixed(8));

export function resolveDesktopArtboardLayout(frame: DesktopFrameSize): DesktopArtboardLayout {
  const width = finitePositive(frame.width);
  const height = finitePositive(frame.height);
  if (width === 0 || height === 0) {
    return { scale: 0, offsetX: 0, offsetY: 0, renderedWidth: 0, renderedHeight: 0 };
  }
  const scale = Math.min(width / DESKTOP_ARTBOARD.width, height / DESKTOP_ARTBOARD.height);
  const renderedWidth = DESKTOP_ARTBOARD.width * scale;
  const renderedHeight = DESKTOP_ARTBOARD.height * scale;
  return {
    scale: rounded(scale),
    offsetX: rounded((width - renderedWidth) / 2),
    offsetY: rounded((height - renderedHeight) / 2),
    renderedWidth: rounded(renderedWidth),
    renderedHeight: rounded(renderedHeight),
  };
}

/** Measure local CSS pixels after heading/layout changes. A highlight ancestor
 * scales its whole page, so its viewport rect must never be applied twice. */
export function applyDesktopArtboardLayout(frame: HTMLElement): DesktopArtboardLayout {
  const computed = frame.ownerDocument?.defaultView?.getComputedStyle(frame);
  const width = Number.parseFloat(computed?.width ?? "") || frame.clientWidth;
  const height = Number.parseFloat(computed?.height ?? "") || frame.clientHeight;
  const layout = resolveDesktopArtboardLayout({ width, height });
  if (layout.scale <= 0) return layout;
  const parent=frame.parentElement,label=parent?.querySelector<HTMLElement>(".uses-desktop-label");
  if(label&&parent){
    label.style.right=`${Math.max(0,parent.clientWidth-frame.offsetLeft-layout.offsetX-layout.renderedWidth)}px`;
  }
  const size = `${width}:${height}`;
  if (frame.dataset.desktopArtboardSize === size) return layout;
  frame.dataset.desktopArtboardSize = size;
  frame.dataset.desktopArtboardMode = "full-fit";
  frame.dataset.desktopArtboardScale = String(layout.scale);
  frame.tabIndex = -1;
  frame.style.setProperty("--uses-desktop-artboard-scale", String(layout.scale));
  frame.style.setProperty("--uses-desktop-artboard-offset-x", `${layout.offsetX}px`);
  frame.style.setProperty("--uses-desktop-artboard-offset-y", `${layout.offsetY}px`);
  return layout;
}
